/**
 * World Selection Screen
 * 
 * Platform-level screen where users choose which adventure world to enter.
 * Currently: Folk World (active) and Lovecraft World (coming soon)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './WorldSelectionScreen.css';
import { AccountSettingsModal } from './AccountSettingsModal';

interface WorldOption {
  id: string;
  name: string;
  description: string;
  imagePath: string;
  isActive: boolean;
  comingSoon?: boolean;
}

const AVAILABLE_WORLDS: WorldOption[] = [
  {
    id: 'folklore',
    name: 'Folk World',
    description: 'Folklore and whispered legends',
    imagePath: '/assets/backgrounds/folkworld-entry.png',
    isActive: true,
  },
  {
    id: 'lovecraft',
    name: 'Lovecraft World',
    description: 'Cosmic horror and eldritch mysteries',
    imagePath: '/assets/backgrounds/lovecraft-entry.png',
    isActive: false,
    comingSoon: true,
  },
  {
    id: 'anime',
    name: 'Anime World',
    description: 'Where the mundane and the magical coexist harmoniously',
    imagePath: '/assets/backgrounds/anime-entry.png',
    isActive: false,
    comingSoon: true,
  },
  {
    id: 'sovereign',
    name: 'Sovereign World',
    description: 'A billion kings with no subjects',
    imagePath: '/assets/backgrounds/sovereign-entry.png',
    isActive: false,
    comingSoon: true,
  },
];

interface WorldSelectionScreenProps {
  onSelectWorld: (worldId: string) => void;
  onLogout?: () => void;
}

export function WorldSelectionScreen({ onSelectWorld, onLogout }: WorldSelectionScreenProps): React.ReactElement {
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="world-selection">
      <div className="world-selection__header">
        <h1 
          onClick={() => navigate('/')} 
          style={{ cursor: 'pointer' }}
          title="Return to home"
        >
          Brynlore
        </h1>
        <p>Choose Your Adventure</p>
        <div className="world-selection__buttons">
          <button className="world-selection__settings" onClick={() => setShowSettings(true)} data-no-sync="true">
            ⚙️ Settings
          </button>
        </div>
      </div>

      <div className="world-selection__grid">
        {AVAILABLE_WORLDS.map((world) => (
          <div
            key={world.id}
            className={`world-card ${!world.isActive ? 'world-card--disabled' : ''}`}
            onClick={() => world.isActive && onSelectWorld(world.id)}
            style={{ cursor: world.isActive ? 'pointer' : 'default' }}
            data-no-sync="true"
          >
            <div className="world-card__image-container">
              <img
                src={world.imagePath}
                alt={world.name}
                className="world-card__image"
              />
              {world.comingSoon && (
                <div className="world-card__badge">Coming Soon</div>
              )}
            </div>
            <div className="world-card__content">
              <h2 className="world-card__title">{world.name}</h2>
              <p className="world-card__description">{world.description}</p>
              {world.isActive && (
                <button className="world-card__button">
                  Enter World
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Account Settings Modal */}
      {showSettings && (
        <AccountSettingsModal
          onClose={() => setShowSettings(false)}
          onAccountDeleted={() => {
            // Account deleted, modal will auto-close
            setShowSettings(false);
          }}
        />
      )}
    </div>
  );
}

